import type {
  BuildingType,
  LocalizedString,
  PlayerId,
  Scenario,
  ScenarioBuilding,
  ScenarioUnit,
  UnitType,
} from './types';
import { TERRAIN_DEFS, BUILDABLE_BUILDING_TERRAINS } from './terrain';
import { UNIT_DEFS } from './units';
import { FACTIONS } from './factions';

export type ValidationResult =
  | { ok: true; scenario: Scenario }
  | { ok: false; errors: string[] };

const TERRAIN_TYPES = new Set<string>(Object.keys(TERRAIN_DEFS));
const UNIT_TYPES = new Set<string>(Object.keys(UNIT_DEFS));
const FACTION_IDS = new Set<string>(FACTIONS.map((f) => f.id));
const BUILDING_TYPES = new Set<BuildingType>([
  'hq', 'city', 'barracks', 'vehicle-factory', 'motor-pool', 'airport', 'port', 'bunker',
]);

const VICTORY_TYPES = new Set([
  'capture-hq', 'eliminate-all', 'survive', 'hold-buildings',
  'kill-unit', 'reach-tile', 'protect-unit', 'capture-building',
  'capture-group', 'hold-building', 'hold-group',
]);
const DEFEAT_TYPES = new Set([
  'unit-killed', 'building-lost', 'turn-limit', 'all-units-killed',
  'lose-group', 'fail-hold-building', 'fail-hold-group',
]);
const EVENT_TRIGGER_TYPES = new Set([
  'turn-start', 'turn-end', 'unit-killed', 'building-captured', 'unit-enters-area',
]);
const EVENT_CONDITION_TYPES = new Set([
  'unit-alive', 'building-owned-by', 'building-neutral', 'turn-ge', 'flag',
]);
const EVENT_ACTION_TYPES = new Set([
  'spawn-units', 'message', 'set-flag', 'remove-unit',
  'change-owner', 'add-gold', 'victory', 'defeat',
]);

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function stringifyValue(v: unknown): string {
  if (v === null) return 'null';
  if (v === undefined) return 'undefined';
  if (typeof v === 'object') return JSON.stringify(v);
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint') return String(v);
  return JSON.stringify(v);
}

function isLocalizedString(v: unknown): v is LocalizedString {
  if (typeof v === 'string') return true;
  if (!isObject(v)) return false;
  for (const key of Object.keys(v)) {
    if (key !== 'pl' && key !== 'en') return false;
    if (v[key] !== undefined && typeof v[key] !== 'string') return false;
  }
  return true;
}

function isInt(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && Math.trunc(v) === v;
}

/** True for `LocalizedString` that has at least one non-empty translation. */
function isNonEmptyLocalizedString(v: unknown): boolean {
  if (typeof v === 'string') return v.length > 0;
  if (!isObject(v)) return false;
  const pl = typeof v.pl === 'string' ? v.pl : '';
  const en = typeof v.en === 'string' ? v.en : '';
  return pl.length > 0 || en.length > 0;
}

/** Validate the optional `buildingGroups` array on a scenario. */
function validateBuildingGroups(
  raw: unknown,
  knownTags: Set<string>,
  errors: string[],
): void {
  if (!Array.isArray(raw)) {
    errors.push('buildingGroups: must be an array');
    return;
  }
  const seenIds = new Set<string>();
  const tagToGroupId = new Map<string, string>();
  raw.forEach((g, i) => {
    const ctx = `buildingGroups[${i}]`;
    if (!isObject(g)) {
      errors.push(`${ctx}: must be an object`);
      return;
    }
    // id
    if (typeof g.id !== 'string' || g.id.length === 0) {
      errors.push(`${ctx}.id: must be a non-empty string`);
    } else if (seenIds.has(g.id)) {
      errors.push(`${ctx}.id: duplicate group id '${g.id}'`);
    } else {
      seenIds.add(g.id);
    }
    // name
    if (!isNonEmptyLocalizedString(g.name)) {
      errors.push(`${ctx}.name: must be a non-empty LocalizedString (at least pl or en)`);
    }
    // buildingTags
    if (!Array.isArray(g.buildingTags) || g.buildingTags.length === 0) {
      errors.push(`${ctx}.buildingTags: must be a non-empty array of tags`);
      return;
    }
    const groupId = typeof g.id === 'string' ? g.id : `index-${i}`;
    for (const tag of g.buildingTags) {
      if (typeof tag !== 'string') {
        errors.push(`${ctx}.buildingTags: every entry must be a string`);
        continue;
      }
      if (!knownTags.has(tag)) {
        errors.push(`${ctx}.buildingTags: unknown tag '${tag}' (no scenario entity carries it)`);
      }
      const previousGroup = tagToGroupId.get(tag);
      if (previousGroup !== undefined && previousGroup !== groupId) {
        errors.push(`${ctx}.buildingTags: tag '${tag}' already belongs to group '${previousGroup}' — groups must be disjoint`);
      } else if (previousGroup === groupId) {
        errors.push(`${ctx}.buildingTags: tag '${tag}' listed twice in the same group`);
      } else {
        tagToGroupId.set(tag, groupId);
      }
    }
  });
}

function validateBuilding(
  b: unknown, idx: number, w: number, h: number, players: Set<PlayerId>,
  terrain: unknown, errors: string[],
): b is ScenarioBuilding {
  const ctx = `buildings[${idx}]`;
  if (!isObject(b)) {
    errors.push(`${ctx}: must be an object`);
    return false;
  }
  let ok = true;
  const inBounds = isInt(b.col) && b.col >= 0 && b.col < w && isInt(b.row) && b.row >= 0 && b.row < h;
  if (!isInt(b.col) || b.col < 0 || b.col >= w) {
    errors.push(`${ctx}.col: out of bounds (got ${String(b.col)}, expected 0..${w - 1})`);
    ok = false;
  }
  if (!isInt(b.row) || b.row < 0 || b.row >= h) {
    errors.push(`${ctx}.row: out of bounds (got ${String(b.row)}, expected 0..${h - 1})`);
    ok = false;
  }
  // Buildings may only sit on base land terrain (plains/coast) — never on
  // object terrains (forest/mountain/road/bridge) or water. A building is a
  // building: the tile beneath it must be a base terrain.
  if (inBounds && Array.isArray(terrain)) {
    const tRow = (terrain as unknown[])[b.row as number];
    const t = Array.isArray(tRow) ? (tRow as unknown[])[b.col as number] : undefined;
    if (typeof t === 'string' && !BUILDABLE_BUILDING_TERRAINS.has(t as never)) {
      errors.push(`${ctx}: building on non-base terrain '${t}' at (${b.col as number},${b.row as number}) — buildings may only sit on plains or coast`);
      ok = false;
    }
  }
  if (typeof b.type !== 'string' || !BUILDING_TYPES.has(b.type as BuildingType)) {
    errors.push(`${ctx}.type: unknown building type '${stringifyValue(b.type)}'`);
    ok = false;
  }
  if (b.owner !== null && !(isInt(b.owner) && players.has(b.owner))) {
    errors.push(`${ctx}.owner: must be null or one of declared players (got ${stringifyValue(b.owner)})`);
    ok = false;
  }
  if (b.tag !== undefined && typeof b.tag !== 'string') {
    errors.push(`${ctx}.tag: must be a string when present`);
    ok = false;
  }
  if (b.name !== undefined) {
    if (!isLocalizedString(b.name)) {
      errors.push(`${ctx}.name: must be a LocalizedString when present`);
      ok = false;
    } else if (!isNonEmptyLocalizedString(b.name)) {
      errors.push(`${ctx}.name: LocalizedString must have at least one non-empty translation (pl or en)`);
      ok = false;
    }
  }
  return ok;
}

function validateUnit(
  u: unknown, idx: number, w: number, h: number, players: Set<PlayerId>,
  errors: string[],
): u is ScenarioUnit {
  const ctx = `units[${idx}]`;
  if (!isObject(u)) {
    errors.push(`${ctx}: must be an object`);
    return false;
  }
  let ok = true;
  if (!isInt(u.col) || u.col < 0 || u.col >= w) {
    errors.push(`${ctx}.col: out of bounds (got ${String(u.col)}, expected 0..${w - 1})`);
    ok = false;
  }
  if (!isInt(u.row) || u.row < 0 || u.row >= h) {
    errors.push(`${ctx}.row: out of bounds (got ${String(u.row)}, expected 0..${h - 1})`);
    ok = false;
  }
  if (typeof u.type !== 'string' || !UNIT_TYPES.has(u.type)) {
    errors.push(`${ctx}.type: unknown unit type '${String(u.type)}'`);
    ok = false;
  }
  if (!isInt(u.owner) || !players.has(u.owner)) {
    errors.push(`${ctx}.owner: must be one of declared players (got ${stringifyValue(u.owner)})`);
    ok = false;
  }
  if (u.tag !== undefined && typeof u.tag !== 'string') {
    errors.push(`${ctx}.tag: must be a string when present`);
    ok = false;
  }
  if (u.name !== undefined && !isLocalizedString(u.name)) {
    errors.push(`${ctx}.name: must be a LocalizedString when present`);
    ok = false;
  }
  if (u.hp !== undefined && (!isInt(u.hp) || u.hp <= 0)) {
    errors.push(`${ctx}.hp: must be a positive integer when present`);
    ok = false;
  }
  if (u.fuel !== undefined && (!isInt(u.fuel) || u.fuel < 0)) {
    errors.push(`${ctx}.fuel: must be a non-negative integer when present`);
    ok = false;
  }
  if (u.ammo !== undefined && (!isInt(u.ammo) || u.ammo < 0)) {
    errors.push(`${ctx}.ammo: must be a non-negative integer when present`);
    ok = false;
  }
  if (u.supply !== undefined) {
    errors.push(`${ctx}.supply: field was split into 'fuel' and 'ammo' — update the scenario`);
    ok = false;
  }
  if (u.cargo !== undefined) {
    if (!Array.isArray(u.cargo)) {
      errors.push(`${ctx}.cargo: must be an array when present`);
      ok = false;
    } else if (typeof u.type === 'string' && UNIT_TYPES.has(u.type)) {
      const def = UNIT_DEFS[u.type as UnitType];
      const capacity = def.cargoCapacity ?? 0;
      const accepts = def.cargoAccepts ?? [];
      if (capacity <= 0) {
        errors.push(`${ctx}.cargo: unit type '${u.type}' cannot carry cargo`);
        ok = false;
      } else if (u.cargo.length > capacity) {
        errors.push(`${ctx}.cargo: ${u.cargo.length} entries exceed capacity ${capacity} of '${u.type}'`);
        ok = false;
      }
      u.cargo.forEach((c, ci) => {
        const cctx = `${ctx}.cargo[${ci}]`;
        if (!isObject(c)) {
          errors.push(`${cctx}: must be an object`);
          ok = false;
          return;
        }
        if (typeof c.type !== 'string' || !UNIT_TYPES.has(c.type)) {
          errors.push(`${cctx}.type: unknown unit type '${String(c.type)}'`);
          ok = false;
        } else if (capacity > 0 && !accepts.includes(c.type as UnitType)) {
          errors.push(`${cctx}.type: '${c.type}' not accepted by '${u.type}' (accepts: ${accepts.join(', ') || 'none'})`);
          ok = false;
        }
        if (c.hp !== undefined) {
          if (!isInt(c.hp) || c.hp <= 0) {
            errors.push(`${cctx}.hp: must be a positive integer when present`);
            ok = false;
          } else if (typeof c.type === 'string' && UNIT_TYPES.has(c.type) && c.hp > UNIT_DEFS[c.type as UnitType].maxHp) {
            errors.push(`${cctx}.hp: ${c.hp} exceeds maxHp ${UNIT_DEFS[c.type as UnitType].maxHp} of '${c.type}'`);
            ok = false;
          }
        }
      });
    }
  }
  return ok;
}

function isPositiveInteger(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= 1;
}

function validateVictoryCondition(
  c: unknown, ctx: string, errors: string[],
  knownTags: Set<string>, knownGroupIds: Set<string>,
): boolean {
  if (!isObject(c) || typeof c.type !== 'string' || !VICTORY_TYPES.has(c.type)) {
    errors.push(`${ctx}: unknown victory condition '${isObject(c) ? String(c.type) : typeof c}'`);
    return false;
  }
  return validateConditionFields(c, ctx, errors, knownTags, knownGroupIds);
}

function validateDefeatCondition(
  c: unknown, ctx: string, errors: string[],
  knownTags: Set<string>, knownGroupIds: Set<string>,
): boolean {
  if (!isObject(c) || typeof c.type !== 'string' || !DEFEAT_TYPES.has(c.type)) {
    errors.push(`${ctx}: unknown defeat condition '${isObject(c) ? String(c.type) : typeof c}'`);
    return false;
  }
  return validateConditionFields(c, ctx, errors, knownTags, knownGroupIds);
}

/** Field-level check for the new group/building condition variants. Other
 *  condition types (legacy capture-hq, hold-buildings…) pass through —
 *  their fields aren't validated here yet. */
function validateConditionFields(
  c: Record<string, unknown>,
  ctx: string,
  errors: string[],
  knownTags: Set<string>,
  knownGroupIds: Set<string>,
): boolean {
  let ok = true;
  const t = c.type as string;
  if (t === 'capture-group' || t === 'hold-group' || t === 'lose-group' || t === 'fail-hold-group') {
    if (typeof c.groupId !== 'string' || c.groupId === '') {
      errors.push(`${ctx}.groupId: must be a non-empty string`); ok = false;
    } else if (knownGroupIds.size > 0 && !knownGroupIds.has(c.groupId)) {
      errors.push(`${ctx}.groupId: unknown group '${c.groupId}' (known: ${[...knownGroupIds].join(', ') || 'none'})`);
      ok = false;
    } else if (knownGroupIds.size === 0) {
      errors.push(`${ctx}.groupId: scenario has no buildingGroups defined`);
      ok = false;
    }
  }
  if (t === 'hold-building' || t === 'fail-hold-building') {
    if (typeof c.buildingTag !== 'string' || c.buildingTag === '') {
      errors.push(`${ctx}.buildingTag: must be a non-empty string`); ok = false;
    } else if (!knownTags.has(c.buildingTag)) {
      errors.push(`${ctx}.buildingTag: unknown tag '${c.buildingTag}'`); ok = false;
    }
  }
  if (t === 'hold-group' || t === 'hold-building' || t === 'fail-hold-group' || t === 'fail-hold-building') {
    if (!isPositiveInteger(c.forTurns)) {
      errors.push(`${ctx}.forTurns: must be a positive integer (>= 1)`); ok = false;
    }
  }
  return ok;
}

function validateEvent(e: unknown, idx: number, errors: string[]): boolean {
  const ctx = `events[${idx}]`;
  if (!isObject(e)) {
    errors.push(`${ctx}: must be an object`);
    return false;
  }
  let ok = true;
  if (!isObject(e.trigger) || typeof e.trigger.type !== 'string' || !EVENT_TRIGGER_TYPES.has(e.trigger.type)) {
    errors.push(`${ctx}.trigger: unknown trigger type`);
    ok = false;
  }
  if (e.conditions !== undefined) {
    if (!Array.isArray(e.conditions)) {
      errors.push(`${ctx}.conditions: must be an array`);
      ok = false;
    } else {
      for (let i = 0; i < e.conditions.length; i++) {
        const c = e.conditions[i];
        if (!isObject(c) || typeof c.type !== 'string' || !EVENT_CONDITION_TYPES.has(c.type)) {
          errors.push(`${ctx}.conditions[${i}]: unknown condition type`);
          ok = false;
        }
      }
    }
  }
  if (!Array.isArray(e.actions)) {
    errors.push(`${ctx}.actions: must be an array`);
    ok = false;
  } else {
    for (let i = 0; i < e.actions.length; i++) {
      const a = e.actions[i];
      if (!isObject(a) || typeof a.type !== 'string' || !EVENT_ACTION_TYPES.has(a.type)) {
        errors.push(`${ctx}.actions[${i}]: unknown action type`);
        ok = false;
      } else if (a.type === 'message' && !isLocalizedString(a.text)) {
        errors.push(`${ctx}.actions[${i}].text: must be a LocalizedString`);
        ok = false;
      }
    }
  }
  return ok;
}

/**
 * Validate a parsed JSON value as a Scenario. Loose: unknown top-level fields
 * are preserved (forward-compat). Strict where it matters: dimensions,
 * bounds, enum membership, owner references.
 */
export function validateScenario(raw: unknown): ValidationResult {
  const errors: string[] = [];

  if (!isObject(raw)) {
    return { ok: false, errors: ['scenario: must be an object'] };
  }

  if (!isLocalizedString(raw.name)) {
    errors.push('name: required, must be a LocalizedString');
  }
  if (raw.description !== undefined && !isLocalizedString(raw.description)) {
    errors.push('description: must be a LocalizedString when present');
  }
  if (raw.story !== undefined && !isLocalizedString(raw.story)) {
    errors.push('story: must be a LocalizedString when present');
  }

  if (!isInt(raw.width) || raw.width <= 0) {
    errors.push(`width: must be a positive integer (got ${String(raw.width)})`);
  }
  if (!isInt(raw.height) || raw.height <= 0) {
    errors.push(`height: must be a positive integer (got ${String(raw.height)})`);
  }
  const w = isInt(raw.width) && raw.width > 0 ? raw.width : 0;
  const h = isInt(raw.height) && raw.height > 0 ? raw.height : 0;

  if (!Array.isArray(raw.terrain)) {
    errors.push('terrain: must be a 2D array');
  } else if (w > 0 && h > 0) {
    if (raw.terrain.length !== h) {
      errors.push(`terrain: expected ${h} rows, got ${raw.terrain.length}`);
    }
    for (let r = 0; r < raw.terrain.length; r++) {
      const row = raw.terrain[r];
      if (!Array.isArray(row)) {
        errors.push(`terrain[${r}]: must be an array`);
        continue;
      }
      if (row.length !== w) {
        errors.push(`terrain[${r}]: expected ${w} cols, got ${row.length}`);
      }
      for (let c = 0; c < row.length; c++) {
        const t = row[c];
        if (typeof t !== 'string' || !TERRAIN_TYPES.has(t)) {
          errors.push(`terrain[${r}][${c}]: unknown terrain '${String(t)}'`);
        }
      }
    }
  }

  if (!Array.isArray(raw.players) || raw.players.length === 0) {
    errors.push('players: must be a non-empty array of integers');
  } else {
    const seen = new Set<number>();
    for (let i = 0; i < raw.players.length; i++) {
      const p = raw.players[i];
      if (!isInt(p)) {
        errors.push(`players[${i}]: must be an integer (got ${String(p)})`);
      } else if (seen.has(p)) {
        errors.push(`players[${i}]: duplicate player id ${p}`);
      } else {
        seen.add(p);
      }
    }
  }
  const players = new Set<PlayerId>(
    Array.isArray(raw.players)
      ? raw.players.filter((p): p is number => isInt(p))
      : [],
  );

  if (!isInt(raw.startingGold) || raw.startingGold < 0) {
    errors.push(`startingGold: must be a non-negative integer (got ${String(raw.startingGold)})`);
  }

  if (raw.factions !== undefined) {
    if (!isObject(raw.factions)) {
      errors.push('factions: must be an object mapping playerId → factionId');
    } else {
      for (const [key, val] of Object.entries(raw.factions)) {
        const pid = Number(key);
        if (!Number.isInteger(pid) || !players.has(pid)) {
          errors.push(`factions['${key}']: key must reference a declared player id`);
        }
        if (typeof val !== 'string' || !FACTION_IDS.has(val)) {
          errors.push(`factions['${key}']: unknown faction id '${stringifyValue(val)}'`);
        }
      }
    }
  }

  if (!Array.isArray(raw.buildings)) {
    errors.push('buildings: must be an array');
  } else if (w > 0 && h > 0) {
    raw.buildings.forEach((b, i) => { validateBuilding(b, i, w, h, players, raw.terrain, errors); });
  }

  if (!Array.isArray(raw.units)) {
    errors.push('units: must be an array');
  } else if (w > 0 && h > 0) {
    raw.units.forEach((u, i) => { validateUnit(u, i, w, h, players, errors); });
  }

  // Tag uniqueness across units + buildings
  const tags = new Set<string>();
  for (const arr of [raw.buildings, raw.units]) {
    if (!Array.isArray(arr)) continue;
    for (const e of arr) {
      if (isObject(e) && typeof e.tag === 'string') {
        if (tags.has(e.tag)) {
          errors.push(`tag collision: '${e.tag}' is used by more than one entity`);
        } else {
          tags.add(e.tag);
        }
      }
    }
  }

  if (raw.buildingGroups !== undefined) {
    validateBuildingGroups(raw.buildingGroups, tags, errors);
  }

  const knownGroupIds = new Set<string>();
  if (Array.isArray(raw.buildingGroups)) {
    for (const g of raw.buildingGroups) {
      if (isObject(g) && typeof g.id === 'string') knownGroupIds.add(g.id);
    }
  }

  if (raw.victoryConditions !== undefined) {
    if (!Array.isArray(raw.victoryConditions)) {
      errors.push('victoryConditions: must be an array');
    } else {
      raw.victoryConditions.forEach((c, i) => {
        validateVictoryCondition(c, `victoryConditions[${i}]`, errors, tags, knownGroupIds);
      });
    }
  }
  if (raw.defeatConditions !== undefined) {
    if (!Array.isArray(raw.defeatConditions)) {
      errors.push('defeatConditions: must be an array');
    } else {
      raw.defeatConditions.forEach((c, i) => {
        validateDefeatCondition(c, `defeatConditions[${i}]`, errors, tags, knownGroupIds);
      });
    }
  }
  if (raw.events !== undefined) {
    if (!Array.isArray(raw.events)) {
      errors.push('events: must be an array');
    } else {
      raw.events.forEach((e, i) => { validateEvent(e, i, errors); });
    }
  }
  if (raw.tutorial !== undefined && !isObject(raw.tutorial)) {
    errors.push('tutorial: must be an object when present');
  }
  if (raw.outcome !== undefined && !isObject(raw.outcome)) {
    errors.push('outcome: must be an object when present');
  }
  if (raw.cameraFocus !== undefined) {
    if (!isObject(raw.cameraFocus) || !isInt(raw.cameraFocus.col) || !isInt(raw.cameraFocus.row)) {
      errors.push('cameraFocus: must be { col: number, row: number }');
    }
  }
  if (raw.editorBackground !== undefined) {
    const bg = raw.editorBackground;
    if (!isObject(bg)) {
      errors.push('editorBackground: must be an object when present');
    } else {
      if (typeof bg.dataUrl !== 'string' || bg.dataUrl === '') {
        errors.push('editorBackground.dataUrl: must be a non-empty string');
      }
      if (typeof bg.offsetX !== 'number' || !Number.isFinite(bg.offsetX)) {
        errors.push('editorBackground.offsetX: must be a finite number');
      }
      if (typeof bg.offsetY !== 'number' || !Number.isFinite(bg.offsetY)) {
        errors.push('editorBackground.offsetY: must be a finite number');
      }
      if (typeof bg.scale !== 'number' || !Number.isFinite(bg.scale) || bg.scale <= 0) {
        errors.push('editorBackground.scale: must be a positive finite number');
      }
      if (bg.opacity !== undefined) {
        if (typeof bg.opacity !== 'number' || bg.opacity < 0 || bg.opacity > 1) {
          errors.push('editorBackground.opacity: must be a number in [0, 1]');
        }
      }
      if (bg.useAsTileset !== undefined && typeof bg.useAsTileset !== 'boolean') {
        errors.push('editorBackground.useAsTileset: must be a boolean');
      }
    }
  }
  if (raw.tilesetImage !== undefined) {
    if (typeof raw.tilesetImage !== 'string' || raw.tilesetImage === '') {
      errors.push('tilesetImage: must be a non-empty string when present');
    }
  }

  if (errors.length > 0) return { ok: false, errors };

  return { ok: true, scenario: raw as unknown as Scenario };
}

